import React, { useState, useContext } from "react";
import { TextField } from "@mui/material";
import { DatasetsByCubeIdDocument, useCreateDatasetMutation, DatasetsByCubeIdQuery } from "graphql/types/graphql";
import { context } from "utils/Provider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useSnackbar } from "notistack";
import { nanoid } from "nanoid";
import colors from "assets/theme-dark/base/colors";
import { styled } from "@mui/system";

interface CreateDatasetFormProps {
  onDatasetCreated: (datasetId?: string) => void;
  disabled: boolean;
}

const StyledCard = styled(MDBox)`
  width: 100%;
  background: ${colors.background.default};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid ${colors.grey[300]};
`;

const CreateDatasetForm: React.FC<CreateDatasetFormProps> = ({ onDatasetCreated, disabled }) => {
  const { selectedCube, setIsLoading } = useContext(context);
  const { enqueueSnackbar } = useSnackbar();
  const [dataSetName, setDataSetName] = useState("");
  const shortID = nanoid(6);

  const sanitizedID = shortID.replace(/-/g, () => {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    return characters[Math.floor(Math.random() * characters.length)];
  });

  const generateDataSetName = (name: string, shortId: string): string => {
    return `${name.toUpperCase()}_${shortId}`;
  };

  const generatedDataSetName = generateDataSetName(dataSetName, sanitizedID);

  const [createDataset, { loading }] = useCreateDatasetMutation({
    update: (cache, { data: { createDataset } }) => {
      const existingData = cache.readQuery<DatasetsByCubeIdQuery>({
        query: DatasetsByCubeIdDocument,
        variables: {
          cubeId: selectedCube.cubeId,
          includeAlreadyMerged: false,
        },
      });

      if (existingData) {
        cache.writeQuery({
          query: DatasetsByCubeIdDocument,
          data: {
            datasetsByCubeId: [
              ...existingData.datasetsByCubeId,
              {
                id: createDataset.id,
                name: generatedDataSetName,
                cubeId: selectedCube.cubeId,
              },
            ],
          },
          variables: {
            cubeId: selectedCube.cubeId,
            includeAlreadyMerged: false,
          },
        });
      }

      cache.evict({
        id: "ROOT_QUERY",
        fieldName: "dataFromTablesByDatasetId",
      });

      cache.evict({
        id: "ROOT_QUERY",
        fieldName: "datasetsByCubeId",
      });

      onDatasetCreated(createDataset.id);
      enqueueSnackbar("Dataset created successfully!", { variant: "success" });
      setDataSetName("");
    },
    onError: (error) => {
      enqueueSnackbar(`Error creating dataset: ${error.message}`, { variant: "error" });
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsLoading(true);

    try {
      await createDataset({
        variables: {
          cubeId: selectedCube.cubeId,
          dataSetName: generatedDataSetName,
        },
      });
    } catch (error: any) {
      enqueueSnackbar("Error creating dataset:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledCard>
      <MDTypography variant="h4" sx={{ color: colors.text.main, mb: 1, ml: "1rem" }}>
        Create Dataset
      </MDTypography>
      <form onSubmit={handleSubmit}>
        <MDBox
          component="div"
          sx={{ display: "flex", flexDirection: "column", gap: 1, width: "30%", marginLeft: "1rem" }}
        >
          <TextField
            label="Dataset Name"
            value={dataSetName}
            onChange={(e) => setDataSetName(e.target.value)}
            required
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderWidth: "0.5px", borderColor: colors.text.main },
                "&:hover fieldset": { borderColor: colors.primary.main },
                "&.Mui-focused fieldset": { borderColor: colors.primary.main },
                color: colors.text.main,
              },
              "& .MuiInputBase-input": { color: colors.text.main },
              "& .MuiInputLabel-root": { color: colors.grey[500] },
              "& .MuiInputLabel-root.Mui-focused": { color: colors.grey[500] },
            }}
            inputProps={{
              style: { color: colors.primary.main },
            }}
          />
          <MDButton
            type="submit"
            variant="background"
            color="secondary"
            disabled={loading || !dataSetName || !selectedCube || disabled}
            sx={{
              width: "fit-content",
              alignSelf: "flex-start",
              borderRadius: "8px",
            }}
          >
            <MDTypography variant="button" fontWeight="medium" color="white">
              Create
            </MDTypography>
          </MDButton>
        </MDBox>
      </form>
    </StyledCard>
  );
};

export default CreateDatasetForm;
